// All page copy, ported verbatim from the design handoff
// (design/One Broadband Landing.dc.html → renderVals()).
import type {
    IAddon, IAdvanceOption, IBearer, IEngageStep, IFaqGroup, INotice, IPhotoSlide,
    IRailItem, IServiceClass, IStackLayer, ITier, IWorkStep, ClassKey, TierKey,
} from '@/types/Landing'

export const NOTICE: INotice = {
    id: 'maint-2026-08-27',
    tag: 'Scheduled maintenance',
    text: 'Planned fibre upgrades in Aberdeen & Lumley, 27 Aug, 01:00–04:00. Brief interruptions possible.',
}

export const COVERAGE_URL = 'https://smrttel.com/portal'
export const PHONE_DISPLAY = '3333 / +232 33 399 399'
export const PHONE_COPY = '+232 33 399 399'
export const EMAIL = 'info@1network.io'
export const ADDRESS = '37A Wilkinson Road, Freetown'

export const RAIL: IRailItem[] = [
    { id: 'top', label: 'Start' },
    { id: 'technology', label: 'Technology' },
    { id: 'offer', label: 'Offerings' },
    { id: 'about', label: 'About us' },
    { id: 'loyalty', label: 'Loyalty' },
    { id: 'faq', label: 'FAQ' },
    { id: 'contact', label: 'Get connected' },
]

// Install figure aligned with the client FAQ (72 h / 3 working days), 2026-08-28.
export const HERO_PROOF = [
    { k: 'Uptime', v: '99.9%' },
    { k: 'Install', v: '72 h' },
    { k: 'Support', v: '24/7' },
]

export const BEARERS: IBearer[] = [
    {
        n: '01', name: 'Fibre', role: 'Our fastest connection, where the network reaches you.', cap: 'Fastest',
        use: 'Households and offices that want the highest speeds we offer, with the most headroom.',
        note: 'Available street by street — we confirm at your exact address before quoting.',
    },
    {
        n: '02', name: 'Wireless', role: 'Strong, stable speeds without waiting for a cable to arrive.', cap: 'Widely available',
        use: 'Addresses fibre has not reached yet, and as a second connection where it has.',
        note: 'Needs a clear line of sight, which the survey checks from your roof.',
    },
    {
        n: '03', name: '5G', role: 'Quick to switch on, ideal for smaller homes and offices.', cap: 'Fast to install',
        use: 'Smaller homes, small offices, and anywhere you need to be online quickly.',
        note: 'Fastest to get running, since there is nothing to dig or mount.',
    },
    {
        n: '04', name: 'Satellite', role: 'Reaches further than anything else, and keeps you online as a backup.', cap: 'Furthest reach',
        use: 'Remote sites, and as the backup that keeps a business trading if the main line drops.',
        note: 'Delivered with our chosen partner, WorldLink — one bill, one number to call.',
    },
]

export const TIERS: Record<TierKey, ITier> = {
    lite: {
        key: 'lite', name: 'One Lite', speed: '15', price: 'NLe 999', priceNum: 999,
        who: 'One or two people, light use',
        router: 'First router free', install: 'Installation NLe 900', installFree: false,
        support: 'Business hours', fair: 'Applies',
        canDo: ['Browsing and social', 'Music and HD video', 'Video calls'],
    },
    base: {
        key: 'base', name: 'One Base', speed: '50', price: 'NLe 1,656', priceNum: 1656,
        who: 'A typical household',
        router: 'First router free', install: 'Free installation', installFree: true,
        support: '24/7', fair: 'None',
        canDo: ['A few people at once', '4K streaming', 'Working from home', 'Gaming'],
    },
    pro: {
        key: 'pro', name: 'One Pro', speed: '100', price: 'NLe 2,376', priceNum: 2376,
        who: 'A busy household',
        router: 'First router free, upgraded', install: 'Free installation', installFree: true,
        support: '24/7', fair: 'None',
        canDo: ['The whole family at once', 'Several 4K streams', 'Big downloads', 'Smart home devices'],
    },
}

export const ADVANCE: IAdvanceOption[] = [
    { key: 'monthly', label: 'Monthly', note: 'Pay as you go', pct: 0 },
    { key: 'm3', label: '3 months', note: 'Save 10%', pct: 10 },
    { key: 'm6', label: '6 months', note: 'Save 20%', pct: 20 },
    { key: 'm12', label: '12 months', note: 'Save 30%', pct: 30 },
]

export const ADDONS: IAddon[] = [
    { key: 'mesh', title: 'Extra Wi-Fi point / mesh', desc: 'Compounds, two-storey houses, thick walls' },
    { key: 'power', title: 'Managed power for the router', desc: 'Keeps you online through outages' },
    { key: 'staticip', title: 'Static IP', desc: 'Home CCTV, home office, remote access' },
]

export const HOME_TERMS = [
    'Shared internet, so speeds are up to the figure shown.',
    'Flexible contract terms.',
    'Free relocation within 72 hours.',
    'Downtime compensated in free days.',
]

// The turn-key delivery stack, bottom-up. Bar width encodes depth of ownership.
export const STACK: IStackLayer[] = [
    { n: '01', t: 'The connection', w: '100%', d: 'Business internet sized to your team and your uptime needs — with a published promise, not a best guess.' },
    { n: '02', t: 'Staying online', w: '86%', d: 'A second connection on a different technology plus managed power, so a cut cable or a dark grid does not stop you trading.' },
    { n: '03', t: 'The network inside', w: '72%', d: 'Router, Wi-Fi, switching and cabling — specified, installed, labelled and documented by our engineers.' },
    { n: '04', t: 'Security', w: '60%', d: 'Managed firewall, endpoint protection, MFA and VPN, configured and kept current rather than handed over as a box.' },
    { n: '05', t: 'Backup and recovery', w: '48%', d: 'Offsite backup and hosted servers, tested, so a bad day is a recovery rather than a loss.' },
    { n: '06', t: 'Running it for you', w: '36%', d: 'Monitoring, patching, service reviews and on-site support. Your team stops being the IT department.' },
]

export const HOW_WE_WORK: IWorkStep[] = [
    {
        n: '01', t: 'We come and survey', d: 'An engineer visits your site, checks what can reach you and where the risks are — before anyone quotes.',
        detail: 'We check line of sight, cable routes, power, rack space and where your people actually sit. You get the findings in writing, including anything we think is a risk.',
    },
    {
        n: '02', t: 'We design it with you', d: 'Sites, users and how much downtime you can tolerate, turned into a written proposal.',
        detail: 'We walk the options through with you — what each layer costs, what it protects, and what you can safely leave out for now.',
    },
    {
        n: '03', t: 'We install and document', d: 'Our own team fits it, tests it and hands over documentation, not a shrug.',
        detail: 'Labelled cabling, tested failover, configuration handed over in writing, and a walkthrough with whoever looks after IT on your side.',
    },
    {
        n: '04', t: 'We stay with you', d: 'Your account manager reviews performance with you and keeps it fit as you grow.',
        detail: 'Monitoring runs continuously, and your account manager brings real uptime figures to each review rather than waiting for you to complain.',
    },
]

export const CLASSES: Record<ClassKey, IServiceClass> = {
    L1: { code: 'L1', name: 'Connect', built: 'SME, 5–25 users', contention: '1:10', sym: 'Asymmetric', fault: 'Business hours', restore: 'Best effort, published', credits: '—', ip: 'Optional', router: 'Optional', failover: 'Optional', power: 'Optional', term: '12 months' },
    L2: { code: 'L2', name: 'Priority', built: 'Growing business, branch, 25–80', contention: '1:5', sym: 'Enhanced upload', fault: 'Extended hours', restore: 'Committed', credits: 'Yes, capped', ip: '1 included', router: 'Included', failover: 'Optional', power: 'Recommended', term: '12 / 24 months' },
    L3: { code: 'L3', name: 'Critical', built: 'Bank, mobile money, mining, hotel', contention: '1:2', sym: 'Near-symmetric', fault: '24/7', restore: 'Committed, tighter', credits: 'Yes', ip: '1 included', router: 'Included', failover: 'Mandatory', power: 'Mandatory', term: '24 / 36 months' },
    L4: { code: 'L4', name: 'Dedicated', built: 'Telco, large enterprise, government', contention: '1:1 committed', sym: 'Full symmetric', fault: '24/7', restore: 'Tightest', credits: 'Negotiated', ip: 'Block included', router: 'Included', failover: 'Mandatory', power: 'Mandatory', term: 'Negotiated' },
}

export const CLASS_ROW_LABELS: [string, keyof IServiceClass][] = [
    ['Built for', 'built'],
    ['Contention (published)', 'contention'],
    ['Symmetry', 'sym'],
    ['Fault window', 'fault'],
    ['Restoration target', 'restore'],
    ['Service credits', 'credits'],
    ['Static IP', 'ip'],
    ['Managed router', 'router'],
    ['Backup connection', 'failover'],
    ['Managed power', 'power'],
    ['Contract term', 'term'],
]

export const WHO_STATS = [
    { v: '4', k: 'Ways to connect you' },
    { v: '24/7', k: 'Local support' },
    { v: '99.9%', k: 'Uptime we design to' },
]

export const PHOTOS: IPhotoSlide[] = [
    { id: 'ob-team-1', placeholder: 'Team photo coming soon', caption: 'Our field team, Freetown.' },
    { id: 'ob-team-2', placeholder: 'Install photo coming soon', caption: 'An install in progress.' },
    { id: 'ob-team-3', placeholder: 'NOC photo coming soon', caption: 'Inside our network operations.' },
    { id: 'ob-team-4', placeholder: 'Customer photo coming soon', caption: 'Customers on the One network.' },
]

export const ENGAGE_CORP: IEngageStep[] = [
    {
        step: 'Step 01', t: 'Feasibility', d: 'We confirm what we can reach your sites on, with a lead time.',
        detail: 'Usually within a few working days. You get a written answer per site, including which options are viable and roughly how long each takes to deliver.',
    },
    {
        step: 'Step 02', t: 'Scope', d: 'Sites, users and the promise you need — mapped to a class and add-ons.',
        detail: 'This is where we agree how much downtime your business can absorb. That single answer drives the service class and whether failover and managed power are mandatory.',
    },
    {
        step: 'Step 03', t: 'Quote & onboard', d: 'Priced from the rate card, then installed, documented and handed over.',
        detail: 'You are introduced to your key account manager at this point, so the person who runs your account has seen the design from the start.',
    },
]

// Client-supplied FAQ (received 2026-08-28), copy kept verbatim apart from typo fixes.
export const FAQ: IFaqGroup[] = [
    {
        key: 'about', title: 'About our fibre internet',
        items: [
            {
                q: 'What is One Broadband?',
                a: 'One Broadband is internet installed directly at your home or office. A dedicated fiber-optic cable connects you to our network, which means fast, stable internet with guaranteed speeds — unaffected by weather, distance from a tower, or how busy the network is.',
            },
            {
                q: 'What makes fiber different from satellite internet (like Starlink)?',
                a: 'Fiber is a physical cable straight to your home, so your speed is guaranteed and unaffected by weather. Satellite signals travel to space and back, which can be affected by weather and congestion, and the equipment has a high upfront cost. With us there is no equipment to buy, and support and payments are local.',
            },
            {
                q: 'How many devices can I connect?',
                a: 'There is no fixed device limit. Keep in mind that your package speed is shared between all connected devices — so the more devices streaming at once, the slower each one will feel. For heavy multi-device use, choose a larger package.',
            },
        ],
    },
    {
        key: 'coverage', title: 'Coverage',
        items: [
            {
                q: 'How do I know if my exact address is covered?',
                a: 'Contact us with your location and we will arrange a free site survey — our technical team confirms coverage for your exact address before you commit to anything.',
            },
        ],
    },
    {
        key: 'connect', title: 'Getting connected',
        items: [
            {
                q: 'How fast will I be installed?',
                a: 'Within 72 hours (3 working days) of your order.',
            },
            {
                q: 'Is there an installation fee?',
                a: 'No — installation is free on all packages except the Lite package.',
            },
            {
                q: 'Do I need to buy a router?',
                a: 'No. The router is provided by us and remains our property. If it ever develops a fault, we swap it free of charge. If you cancel your service, the equipment is returned to us (unless you purchased it).',
            },
            {
                q: 'Is there a contract?',
                a: 'Yes — contracts run for 1 year, and your contract and SLA are available to you. All our prices are tax-inclusive.',
            },
            {
                q: 'I’m moving house — can I take my service with me?',
                a: 'Yes, if your new address is in our coverage area. Relocation is free and completed within 72 hours.',
            },
        ],
    },
    {
        key: 'billing', title: 'Billing & payments',
        items: [
            {
                q: 'How does billing work?',
                a: 'Residential service is prepaid: your package runs for its period and service pauses when it expires, so renew before your expiry date. You can check your expiry date any time on the customer portal, and we send you a reminder before it. Once you pay, service is activated the same day. Corporate customers are served postpaid through their Key Account Manager.',
            },
            {
                q: 'How can I pay?',
                a: 'Six channels, most of them instant:',
                table: [
                    ['Mobile Money', 'Instant'],
                    ['One Broadband App', 'Instant'],
                    ['Vult', 'Instant'],
                    ['Bank transfer', '3 days'],
                    ['Cheque', '3 days'],
                    ['Cash at our showroom', 'Instant'],
                ],
            },
            {
                q: 'Can family abroad pay for my internet?',
                a: 'Yes — payments from abroad work through Vult and Paytag, using your account details as the reference.',
            },
            {
                q: 'How do I cancel?',
                a: 'You have 30 days to cancel. Our equipment is returned to us after cancellation, unless you purchased your devices.',
            },
        ],
    },
    {
        key: 'wifi', title: 'Your router & Wi-Fi',
        items: [
            {
                q: 'Can I change my WiFi name and password?',
                a: 'Router settings on fiber connections are managed by us to keep your connection secure and supported — contact our team on 3333 and we’ll help you with any changes.',
            },
            {
                q: 'WiFi is weak in parts of my home — what can I do?',
                a: 'Position makes the biggest difference: place the router centrally and elevated, out of cabinets, and away from thick concrete walls, metal doors, and fridges. If speed is good next to the router but weak far away, it’s a coverage question — contact us about options for larger homes.',
            },
        ],
    },
    {
        key: 'support', title: 'Problems & support',
        items: [
            {
                q: 'My internet is not working — what should I check first?',
                a: 'Three quick checks solve most cases: (1) Is your subscription active? An expired package is the most common cause. (2) Is the router powered on? Restart it and wait 2–3 minutes. (3) Check the light on your fiber device — green means good network; red means no optical signal, so call us and we’ll take it from there.',
            },
            {
                q: 'How do I report a fault, and how fast will it be fixed?',
                a: 'Call our customer centre on 3333 or 033 399 399 (corporate customers can also contact their Key Account Manager). Faults are resolved within 2 days, and we keep you updated by call and text.',
            },
            {
                q: 'Will I be compensated if my service is down?',
                a: 'Yes — when an outage is our fault, we add free days to your subscription for the days you lost.',
            },
        ],
    },
]

export const CONTACT_CHANNELS: [string, string][] = [
    ['Customer centre (24/7)', '3333'],
    ['Phone', '033 399 399'],
    ['WhatsApp', '088 399 399'],
    ['Email', 'support@1network.io'],
    ['Headquarters (8am – 6pm)', '37A Wilkinson Road, Freetown'],
]

export const ENGAGE_RETAIL: IEngageStep[] = [
    {
        step: 'Step 01', t: 'Tell us your address', d: 'We confirm what we can reach you on and what is available.',
        detail: 'You can also check the coverage map yourself first — but a quick call is usually faster, since our footprint changes every month.',
    },
    {
        step: 'Step 02', t: 'We call and install', d: 'We book a time and handle the installation — free on Base and Pro.',
        detail: 'Installs happen within 72 hours of your order, and we work around a time that suits your household rather than a vague window.',
    },
    {
        step: 'Step 03', t: 'Get playing', d: '24/7 support, and free relocation when you move.',
        detail: 'Your first router is included. If you move house, we relocate the connection free within 72 hours anywhere we reach.',
    },
]
